import classNames from "classnames";
import { isToday } from "date-fns";
import { Range } from "immutable";
import { observer } from "mobx-react";
import React, { useRef, useState } from "react";
import { FormattedDate } from "react-intl";
import { DefaultErrorFallback, withErrorBoundary } from "@fidget/common/components/errorBoundary";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { zeroPad } from "@fidget/common/utils";
import { getTimeZoneString } from "@fidget/common/utils/timeFormatter";
import { CalendarColumn } from "../../components/calendar/calendarColumn";
import columnStyles from "../../components/calendar/calendarColumn.css";
import { CalendarDay } from "../../components/calendar/calendarDay";
import { CalendarNavigationBar } from "../../components/calendar/calendarNavigationBar";
import { MeetingPopup } from "../../components/calendar/meetingPopup";
import styles from "./calendar.css";
import { TrashBar } from "./trashBar";
import { TimeUtils } from "@fidget/common/utils/timeUtils";
import { useStore } from "../../models";
import { withPermissionFallback } from "../../components/HOC/withPermissionFallback/withPermissionFallback";
import { staticConfig } from "@fidget/common/config/static-config";
import { AppPermission } from "@closerplatform/spinner-openapi";

export interface Props {
  weekly: boolean;
}

export const CalendarComponent = observer(({ weekly }: Props) => {
  const { calendarStore, currentUserStore } = useStore();
  const calendarScrollContainer = useRef<Element>();
  const [autoScrollTimer, setAutoScrollTimer] = useState<number>();
  const [dragTopHovered, setDragTopHovered] = useState(false);
  const [dragBottomHovered, setDragBottomHovered] = useState(false);

  const scrollToTop = () => {
    if (calendarScrollContainer.current) {
      if (calendarScrollContainer.current.scrollTop > staticConfig.calendar.scrollMin) {
        calendarScrollContainer.current.scrollTop = Math.max(
          calendarScrollContainer.current.scrollTop - staticConfig.calendar.scrollStep,
          staticConfig.calendar.scrollMin
        );
      }
    }
  };

  const scrollToBottom = () => {
    if (calendarScrollContainer.current) {
      const scrollMax =
        calendarScrollContainer.current.scrollHeight - calendarScrollContainer.current.clientHeight;
      if (calendarScrollContainer.current.scrollTop < scrollMax) {
        calendarScrollContainer.current.scrollTop = Math.min(
          calendarScrollContainer.current.scrollTop + staticConfig.calendar.scrollStep,
          scrollMax
        );
      }
    }
  };

  const onMouseMove = (ev: React.MouseEvent<HTMLDivElement>) => {
    const { draggingMeeting } = calendarStore;
    if (draggingMeeting) {
      if (
        ev.nativeEvent.clientY <
        ev.currentTarget.getBoundingClientRect().top + staticConfig.calendar.scrollTopOffset
      ) {
        if (!dragTopHovered) {
          setDragTopHovered(true);
          scrollToTop();
          setAutoScrollTimer(
            window.setInterval(scrollToTop, staticConfig.calendar.scrollTimerInterval)
          );
        }
      } else if (dragTopHovered) {
        setDragTopHovered(false);
        clearAutoScrollTimer();
      }
      if (
        ev.nativeEvent.clientY >
        ev.currentTarget.getBoundingClientRect().top +
          ev.currentTarget.getBoundingClientRect().height -
          staticConfig.calendar.scrollBottomOffset
      ) {
        if (!dragBottomHovered) {
          setDragBottomHovered(true);
          scrollToBottom();
          setAutoScrollTimer(
            window.setInterval(scrollToBottom, staticConfig.calendar.scrollTimerInterval)
          );
        }
      } else if (dragBottomHovered) {
        setDragBottomHovered(false);
        clearAutoScrollTimer();
      }
    } else {
      clearAutoScrollTimer();
      setDragBottomHovered(false);
      setDragTopHovered(false);
    }
  };

  const clearAutoScrollTimer = () => {
    clearInterval(autoScrollTimer);
    setAutoScrollTimer(undefined);
  };

  const onMouseUp = () => {
    clearAutoScrollTimer();
  };

  const onMouseLeave = () => {
    clearAutoScrollTimer();
  };

  const renderDayHeader = (date: Date) => {
    const isStartToday = isToday(date);
    return (
      <div
        key={date.valueOf()}
        className={classNames(columnStyles["calendar-column"], {
          [columnStyles["calendar-column-today"]]: isStartToday,
          [columnStyles["calendar-column-highlighted"]]: isStartToday && weekly
        })}
      >
        <div className={columnStyles["day-header"]} key={date.valueOf()}>
          <div>
            {isToday(date) ? (
              intl.formatMessage("date.today")
            ) : (
              <FormattedDate value={date} weekday={"long"} />
            )}
          </div>
          <div>
            <FormattedDate value={date} day={"2-digit"} month={"2-digit"} />
          </div>
        </div>
      </div>
    );
  };

  const render = () => {
    const { displayedDay } = calendarStore;
    const days = TimeUtils.datesRange(displayedDay, weekly);
    const workingHours = [...currentUserStore.workingHours];
    workingHours.unshift(workingHours.pop() || { from: 0, to: 24 });

    return (
      <div
        className={styles["calendar-container"]}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
      >
        {<MeetingPopup />}
        <CalendarNavigationBar weekly={weekly} showTodayButton={!days.some((_) => isToday(_))} />
        <div
          className={classNames(styles["calendar-body-scroll-container"], {
            [styles.weekly]: weekly
          })}
          ref={(el) => (calendarScrollContainer.current = el || undefined)}
        >
          <div className={styles["calendar-header"]}>
            <div className={columnStyles["calendar-column-time"]}>
              <div className={columnStyles["calendar-header"]}>{getTimeZoneString()}</div>
            </div>
            {days.map((date) => renderDayHeader(date)).toArray()}
          </div>
          <div className={styles["calendar-body"]}>
            {weekly && (
              <div className={styles["working-hours-text"]}>
                {intl.formatMessage("calendar.workingHours")}
              </div>
            )}
            <CalendarColumn
              className={columnStyles["calendar-column-time"]}
              isToday={false}
              entries={[]}
              googleEntries={[]}
            >
              {Range(0, 24)
                .map((h) => (
                  <div
                    key={h}
                    className={classNames(styles["hours-cell"], {
                      [styles["outside-working-hours"]]:
                        h < workingHours[0]!.from || h >= workingHours[0]!.to
                    })}
                  >
                    {`${zeroPad(h, 2)}:00`}
                  </div>
                ))
                .toArray()}
            </CalendarColumn>
            {days
              .map((date) => (
                <CalendarDay
                  key={date.valueOf()}
                  start={date}
                  weekly={weekly}
                  workingHours={workingHours[date.getDay()]!}
                />
              ))
              .toArray()}
          </div>
        </div>
        <TrashBar />
      </div>
    );
  };

  return render();
});

export const Calendar = withPermissionFallback(
  AppPermission.MeetingsPageVisibility,
  withErrorBoundary(CalendarComponent, () => (
    <div className={styles["error-container"]}>
      <DefaultErrorFallback />
    </div>
  ))
);
